import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CustomButton } from '../Button/Button';
import Icon from '../Icon/Icon';
import styles from './AddClipAndTransitionButton.module.scss';
import cx from 'classnames';
import CardMenu from '../CardMenu/CardMenu';
import useOutsideClick from '../../hooks/useOutsideClick';
import { useTranslation } from 'react-i18next';

const AddClipAndTransitionButton = ({
  index,
  type = 'vertical',
  before = false,
  lastCard = false,
  showAddTransition = true,
  showAddItem = true,
  handleAddTransition,
  handleAddClip = () => { },
  handleAddEmptyClip = () => { },
  handleUploadClip,
  scroll = false,
  isEditingAction,
  handleRecordScene,
  disabled,
  changeExternalAddMenuShow = () => { },
  hoverCardId = null,
  mouseInScrollSlider,
  hasTransitionAfter,
  hasChanged,
}) => {
  const [visible, setVisible] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [menuCoordinates, setMenuCoordinates] = useState({ x: 0, y: 0 });

  const { t } = useTranslation();

  const onMouseOver = useCallback(() => {
    !disabled && !lastCard && setVisible(true);
  }, [disabled, lastCard]);

  const onMouseLeave = useCallback(() => {
    !disabled && !lastCard && !showMenu && setVisible(false);
  }, [disabled, lastCard]);

  const menuRef = useRef();

  const handleMenuOpen = e => {
    if (disabled) {
      return;
    }
    setShowMenu(true);
    const current = e.currentTarget.getBoundingClientRect();
    setMenuCoordinates({ x: e.clientX, y: Math.round(current.top) });
  };

  const handleCloseButtonActions = () => {
    setShowMenu(false);
    setVisible(false);
  };

  const onAddClip = () => {
    handleAddClip(index, before);
  };

  const onAddEmptyClip = () => {
    handleAddEmptyClip(index, before);
  };

  const onAddTransition = () => {
    handleAddTransition && handleAddTransition(index, before);
  };

  useOutsideClick(menuRef, () => {
    if (showMenu) {
      handleCloseButtonActions();
    }
  });

  useEffect(() => {
    if (scroll && showMenu) {
      handleCloseButtonActions();
    }
  }, [scroll]);

  useEffect(() => {
    changeExternalAddMenuShow(showMenu);
  }, [showMenu]);

  return (
    <>
      <div
        className={cx(styles.btnContainer, styles[type], {
          [styles.visible]:
            (visible ||
              lastCard ||
              (hoverCardId === index && mouseInScrollSlider) ||
              (hoverCardId - 1 === index && !before && mouseInScrollSlider && !hasTransitionAfter)) &&
            !disabled &&
            !isEditingAction,
          [styles.disabled]: disabled || isEditingAction,
        })}
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseLeave}
        onClick={handleMenuOpen}
      >
        {visible ||
          lastCard ||
          (hoverCardId === index && mouseInScrollSlider) ||
          (hoverCardId - 1 === index && !before && mouseInScrollSlider && !hasTransitionAfter) ? (
          <>
            <div className={cx(styles.line, styles[type])} />
            <CustomButton variant="primary" className={styles.addButton} ariaLabel={t('general.add')}>
              <Icon src="addIconWhite" className={styles.addIcon} />
            </CustomButton>
          </>
        ) : null}
      </div>
      {showMenu && (
        <CardMenu
          customStyles={{ top: menuCoordinates.y, left: menuCoordinates.x, position: 'fixed', transform: 'translate(0, 50%)' }}
          menuRef={menuRef}
          itemIndex={index}
          item={{ position: before }}
          addTransition={showAddTransition && ((index > 0 && before) || (!lastCard && !before)) && onAddTransition}
          addItem={showAddItem && onAddClip}
          addEmptyItem={onAddEmptyClip}
          recordItem={{
            action: handleRecordScene,
            disabled: hasChanged,
            message: t('clip_organizer.add_menu.record_disabled_tooltip'),
          }}
          uploadItem={{
            action: handleUploadClip,
            disabled: hasChanged,
            message: t('clip_organizer.add_menu.upload_disabled_tooltip'),
          }}
          toggleShowMenu={handleCloseButtonActions}
          topOffset={!(type === 'horizontal')}
          rightOffset={true}
        />
      )}
    </>
  );
};

export default AddClipAndTransitionButton;
